import { useEffect, useRef } from 'react';
import { useMap } from 'react-map-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { useLassoTool } from '../../application/lassoToolStore';
/*
MapboxDraw is an imperative plugin for mapbox-gl, not React-aware.

React-Map-GL does not natively support MapboxDraw, so you need a small bridge component that:
1.Initializes the draw tool on the map
2.Cleans it up on unmount
3.Wires up event handlers (onCreate, onUpdate, onDelete)
4.Accepts custom modes (e.g. FreehandPolygonMode)
*/

export interface DrawControlProps {
  displayControlsDefault?: boolean;
  modes?: any;
  onCreate?: (e: any) => void;
  onUpdate?: (e: any) => void;
  onDelete?: (e: any) => void;
}

const DrawControl = ({
  displayControlsDefault = false,
  modes,
  onCreate,
  onUpdate,
  onDelete,
}: DrawControlProps) => {
  const draw = useRef<MapboxDraw>();
  const { current: map } = useMap();
  const setDraw = useLassoTool((s) => s.setDraw);

  useEffect(() => {
    if (!map) return;

    draw.current = new MapboxDraw({
      displayControlsDefault,
      modes,
    });

    map.addControl(draw.current as any);

    if (onCreate) map.on('draw.create', onCreate);
    if (onUpdate) map.on('draw.update', onUpdate);
    if (onDelete) map.on('draw.delete', onDelete);

    if (draw.current !== undefined && draw.current !== null) {
      setDraw(draw.current);
    }

    return () => {
      const m = map?.getMap?.();

      if (!m || !draw.current) {
        draw.current = undefined;
        setDraw(null);
        return;
      }
      try {
        if (onCreate) m.off('draw.create', onCreate);
        if (onUpdate) m.off('draw.update', onUpdate);
        if (onDelete) m.off('draw.delete', onDelete);
      } catch (error) {
        console.error('Error removing draw control: ', error);
      }

      try {
        m.removeControl(draw.current as any);
      } catch {
        // mapbox-gl-draw's onRemove can throw during teardown; safe to ignore
      }

      draw.current = undefined;
      setDraw(null);
    };
  }, [map]);

  return null;
};

export default DrawControl;
